import { MouseEvent, useEffect, useState } from 'react'
import { TFunction } from 'react-i18next'
import { toast } from 'react-toastify'
import ColorHash from 'color-hash'
import { format, parseJSON } from 'date-fns'
import dayjs from 'dayjs'
import noLocale from 'dayjs/locale/nb'
import { t } from 'i18next'
import { createFilterOptions } from '@mui/material/useAutocomplete'
import appConfig from '../config'
import config from '../config'
import {
  AssetType,
  MainVesselType,
  ProductRequestStatus,
  RiskAnalysisRiskStatus,
  WorkshopBoatStatus,
  WorkshopWorkOrderSection,
} from '../enums'
import type { IAssetDataLabel } from '../http'
import { Service } from '../Http/DataTypes'
import {
  ColorName,
  ColorUser,
  IAsset,
  IBoat,
  IFile,
  IFileFolder,
  IWorkOrder,
  IWorkshopStatus,
  IWorkshopUser,
} from '../types/data-types'
import { getActiveRefreshToken } from './AuthorizationHelpers'
import { axios } from './axios'
import { showConfirmDialog } from './message'
import { decode, stripHTML } from './QuillEditor'

export function loadLocale() {
  try {
    const locale = localStorage.getItem('language')
    if (locale) return locale

    return 'en'
  } catch (e) {
    saveLocale('en')
    return 'en'
  }
}

// Debounce makes it so that the state value does not change until a set amount of time has passed.
// useful for when you don't want changes to happen instantly, like showing suggestions when searching.
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function saveLocale(locale) {
  axios.defaults.headers['Accept-Language'] = locale
  localStorage.setItem('language', locale)
}

export const dayjsLocaleMap = {
  en: undefined,
  nb: noLocale,
}

export const capitalizeFirst = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1)

export function getMsgFromAxiosErr(err, key = 'error') {
  const isNorwegian = loadLocale() == 'nb'
  if (!err?.response) return isNorwegian ? 'En feil har oppstått' : 'An error has occurred'
  const { data, status } = err.response

  if (status === 500) return isNorwegian ? 'En feil har oppstått' : 'An error has occurred'

  if (key && data[key]) return data[key]

  return data
}

export function getApiErrorMessage(error: any, translate: TFunction): string {
  const backendErrorKey = error?.response?.data

  if (typeof backendErrorKey === 'string' && backendErrorKey.trim() !== '') {
    const translationKey = backendErrorKey.replace('.', ':')
    return translate(translationKey)
  }

  return translate('common:failed-operation-message')
}

export function getRequestChipClass(status) {
  switch (status) {
    case ProductRequestStatus.Approved:
      return 'chip chip__success'
    case ProductRequestStatus.Rejected:
      return 'chip chip__danger'

    default:
      return 'chip chip__neutral'
  }
}

export const imageMimeTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/*']

export const formatDueDateOnly = (dateString: string): string => {
  if (!dateString) return ''

  const date = new Date(dateString)
  date.setDate(date.getDate() + 1)
  return date.toLocaleDateString()
}

export function isFileImage(file: IFile) {
  if (!file) return false

  return imageMimeTypes.some(supportedMime => file.contentType?.includes(supportedMime))
}

const colorHash = new ColorHash()
/**
 * Returns a unique color for a user based on username and userid
 */
// for userColor to work we need a user object with an id and (fullname or firstName and lastName)
export const userColor = (user: ColorUser): string => {
  const name = user.fullname ? user.fullname : (user.firstName || '') + (user.lastName || '')
  return colorHash.hex((name || '').replace(/\s/g, '') + user.userId)
}

// for nameColor to work we need a user object with fullname or (firstName and lastName)
export const nameColor = (user: ColorName): string => {
  const name = user.fullname ? user.fullname : user.firstName + user.lastName
  return colorHash.hex(name)
}

export const stringColor = (str: string): string => colorHash.hex(str)

export const userColorByName = (name: string, userId: number): string => {
  return colorHash.hex(name?.replace(/\s/g, '') + userId)
}

export async function readFileToArrayBuffer(file) {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)

    reader.readAsDataURL(file)
  })
}

export function base64FileToblob(file) {
  if (!file?.fileContent) {
    const parsedFile = new File([], file.filename, { type: file.mime })
    // @ts-ignore
    parsedFile.id = file.fileId
    return parsedFile
  }

  const bstr = atob(file.fileContent.contentData)
  const u8arr = new Uint8Array(file.fileContent.contentData.length)

  for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i)

  const parsedFile = new File([u8arr], file.filename, { type: file.mime })
  // @ts-ignore
  parsedFile.id = file.fileId

  return parsedFile
}

const dateFormat = 'dd-MM-yyyy'
const dateTimeFormat = dateFormat + ' : HH:mm'
const dateTimeFormatForParsing = 'MM-dd-yyy : HH:mm'

export function formatDate(date: string | Date, formatString?: string) {
  if (typeof date === 'string') date = new Date(date)

  try {
    return format(date, formatString ?? dateFormat)
  } catch {
    return '...'
  }
}

/**
 * Formats a date object to a string using the provided format string.
 * @param dateObj Date object to format
 * @param formatString Format string (default: 'yyyy-MM-dd')
 * @returns Formatted date string or null if input is invalid
 */
export function formatDateOnly(dateObj: Date | null | undefined, formatString = 'yyyy-MM-dd') {
  if (!dateObj) return null
  try {
    return format(dateObj, formatString)
  } catch {
    return null
  }
}

export function formatDateTime(date) {
  if (typeof date === 'string') date = new Date(date)

  return format(date, dateTimeFormat)
}

export function formatDateTimeFromUTC(date, customFormat?: string) {
  if (typeof date === 'string') date = parseJSON(date)

  return format(date, customFormat ?? dateTimeFormat)
}
export function formatDateTimeFromUTCForParsing(date, customFormat?: string) {
  if (typeof date === 'string') date = parseJSON(date)

  return format(date, customFormat ?? dateTimeFormatForParsing)
}

export function formatDateFromUTC(date, customFormat?: string) {
  if (!date) return ''
  if (typeof date === 'string') date = parseJSON(date)
  if (!date || isNaN(date.getTime())) return ''

  return format(date, customFormat ?? dateFormat)
}

export function parseDate(date: string): Date | null {
  if (!date) return null
  try {
    const parsed = parseJSON(date)
    if (parsed && !isNaN(parsed.getTime())) {
      return parsed
    }
    const fallback = new Date(date)
    if (!isNaN(fallback.getTime())) {
      return fallback
    }
    return null
  } catch {
    return null
  }
}

/**
 * Normalizes a date to the start of day (00:00:00) for accurate day comparisons
 * @param date Date to normalize
 * @returns New Date object set to start of day
 */
export function normalizeToStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function stringToBool(str) {
  switch (str) {
    case 'true':
      return true
    case 'false':
      return false
    default:
      return undefined
  }
}

const defaultFilter = createFilterOptions()

export const autoCompleteCreateFilter = (opts, params, filter = defaultFilter) => {
  const filtered = filter(opts, params)

  if (params.inputValue !== '') {
    filtered.push({
      inputValue: params.inputValue,
      name: `Create '${params.inputValue}'`,
    })
  }

  return filtered
}

export function copyToClipboard(text) {
  if (!navigator.clipboard) return fallbackCopyToClipboard(text)

  return navigator.clipboard.writeText(text)
}

function fallbackCopyToClipboard(text) {
  const el = document.createElement('textarea')
  el.value = text
  el.style.top = '0'
  el.style.left = '0'
  el.style.position = 'fixed'
  document.body.appendChild(el)

  el.focus()
  el.select()

  document.execCommand('copy')
}
export const newShade = (hexColor, magnitude) => {
  hexColor = hexColor.replace('#', '')
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16)
    let r = (decimalColor >> 16) + magnitude
    r > 255 && (r = 255)
    r < 0 && (r = 0)
    let g = (decimalColor & 0x0000ff) + magnitude
    g > 255 && (g = 255)
    g < 0 && (g = 0)
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude
    b > 255 && (b = 255)
    b < 0 && (b = 0)
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`
  } else {
    return hexColor
  }
}
export async function confirmDeleteWrapper(t, deleteFunc) {
  const confirmed = await showConfirmDialog(t('common:warning'), t('actions:delete-warning'), {
    acceptText: t('common:delete'),
  })
  if (!confirmed) return

  return deleteFunc()
}

export function strToBool(str) {
  switch (str) {
    case 'True':
    case 'true':
    case true:
      return true
    case 'False':
    case 'false':
    case false:
      return false
    default:
      return false
  }
}

export const removeDuplicatesInArray = arr => {
  return arr.filter((item, index) => arr.indexOf(item) === index)
}

export function validateHourCounterLogMaxValue(value, lastLog) {
  const lastRecordDate = new Date(lastLog.registeredDate)
  // @ts-ignore
  const maxHours = Math.floor(Math.abs(new Date(new Date().toUTCString()) - lastRecordDate) / 36e5)

  const maxAllowed = lastLog.value + maxHours

  return value > maxAllowed ? `Verdien må være mellom (${lastLog.value} og ${maxAllowed}]` : true
}

export const reorderArray = <T>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export function stopPropagationEventHandler(e: MouseEvent) {
  return e.stopPropagation()
}

export function getRiskAnalysisStatusText(status: RiskAnalysisRiskStatus | number) {
  if (!status) return 'not-rated'
  else if (status <= RiskAnalysisRiskStatus.Low) return 'low'
  else if (status <= RiskAnalysisRiskStatus.Moderate) return 'medium'
  else if (status > RiskAnalysisRiskStatus.Moderate) return 'high'
}

export function matchRiskAnalysisRiskLevel(riskLevel: number, { onNotRated, onLow, onModerate, onHigh }) {
  if (!riskLevel) return onNotRated
  else if (riskLevel <= RiskAnalysisRiskStatus.Low) return onLow
  else if (riskLevel <= RiskAnalysisRiskStatus.Moderate) return onModerate
  else if (riskLevel > RiskAnalysisRiskStatus.Moderate) return onHigh
}

export function matchRiskAnalysisFieldLevel(riskLevel: number, { onNotRated, onLow, onMedium, onModerate, onHigh }) {
  switch (riskLevel) {
    case 0:
      return onNotRated
    case 1:
      return onLow
    case 2:
      return onMedium
    case 3:
      return onModerate
    case 4:
      return onHigh
  }
}

export function NOOP() {}

export function capitalizeFirstLetter(value) {
  if (!value || value == undefined) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function capitalizeFirstLetterSentence(value?: string) {
  if (!value || value.trim() == '') return ''
  let returnWord = ''
  const wordsArray = value.split(' ')
  if (wordsArray.length == 0) return ''
  wordsArray.forEach((word, index) => {
    returnWord += capitalizeFirstLetter(word) + (wordsArray.length - 1 == index ? '' : ' ')
  })
  return returnWord
}

export function getUserDisplayText(user) {
  if (user?.fullname && user?.fullname.trim() != '') {
    return capitalizeFirstLetterSentence(user?.fullname)
  } else if ((user?.firstName && user?.firstName.trim() != '') || (user?.lastName && user?.lastName.trim() != '')) {
    return `${capitalizeFirstLetterSentence(user?.firstName)} ${capitalizeFirstLetterSentence(user?.lastName)}`
  }
  return user?.email ?? ''
}

export function getFileDownloadPathFromToken(viewToken: string) {
  return `${appConfig.api.url}/api/file/v2/download-file/${viewToken}`
}

export const getUsersAsCommaSeparatedList = (workshopUsers: IWorkshopUser[]) => {
  const userList = workshopUsers?.map(u => u.user.fullname).join(', ')
  return userList || t('not-applicable')
}

export function truncate(str, n) {
  if (!str) return
  return str.length > n ? str.substr(0, n - 3) + '...' : str
}

export function getFileDownloadPath(file: IFile) {
  return `${appConfig.api.url}/api/file/v2/download-file/${file?.viewToken}`
}
export function getFileThumbnailDownloadPath(file: IFile) {
  return `${appConfig.api.url}/api/file/v2/download-file/${file?.viewToken}?thumbnail=true&preview=true`
}

export function getFilePreviewPath(file: IFile) {
  return `${appConfig.api.url}/api/file/v2/download-file/${file?.viewToken}?preview=true`
}

export function previewFileWithToken(viewToken: string) {
  return openLinkInNewTab(getFileDownloadPathFromToken(viewToken))
}

export function getFolderDownloadPath(folderId: EntityId) {
  const refreshToken = getActiveRefreshToken()
  return `${appConfig.api.url}/api/file/folders/${folderId}/download?token=${refreshToken}`
}

export function getFamiliarizationZipDownloadPath(familiarizationFormId: number, revision: number) {
  return `${appConfig.api.url}/api/report/familiarization/${familiarizationFormId}/${revision}`
}

export function isObjectEmpty(object: AnyObject) {
  return Object.keys(object).length === 0
}

export function previewFile(file: IFile) {
  return openLinkInNewTab(getFileDownloadPath(file))
}

export function downloadFile(file: IFile) {
  return openLinkInNewTab(getFileDownloadPath(file), true)
}

export function truncateText(text: string, maxLength: number) {
  if (text?.length > maxLength) {
    return text.substring(0, maxLength) + '...'
  }
  return text
}
export function downloadFolderAuthorized(folder: IFileFolder, responseType = 'blob') {
  const anchor = document.createElement('a')
  document.body.appendChild(anchor)

  axios
    .get(`/api/file/folders/${folder.id}/download`, {
      responseType: responseType as any,
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', folder.name + '.zip')
      document.body.appendChild(link)
      link.click()
    })
    .catch(error => {
      toast.error(error.message)
    })
}

export function downloadFileAuthorized(
  path: string,
  filename: string,
  params = undefined,
  finishedDownloaded = () => {},
  responseType = 'blob',
) {
  const anchor = document.createElement('a')
  document.body.appendChild(anchor)

  axios
    .get(path, {
      responseType: responseType as any,
      params: params,
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      finishedDownloaded()
    })
    .catch(error => {
      toast.error(error.message)
      finishedDownloaded()
    })
}

export function downloadFileAuthorizedWithBody(
  path: string,
  filename: string,
  data: any,
  finishedDownloaded = () => {},
) {
  const anchor = document.createElement('a')
  document.body.appendChild(anchor)

  axios
    .post(path, data, {
      responseType: 'blob' /* or responseType: 'arraybuffer'  */,
    })
    .then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename) //or any other extension
      document.body.appendChild(link)
      link.click()
      finishedDownloaded()
    })
    .catch(error => toast.error(error.message))
    .finally(() => finishedDownloaded())
}

export function downloadFolder(folder: IFileFolder) {
  return openLinkInNewTab(getFolderDownloadPath(folder.id))
}

export function downloadFamiliarizationZip(familiarizationFormId: number, revision: number) {
  return openLinkInNewTab(getFamiliarizationZipDownloadPath(familiarizationFormId, revision))
}

export function stringifyParams(obj: { [key: string]: any }) {
  if (!obj) return ''

  return Object.entries(obj).reduce((acc, curr, idx, array) => {
    const [key, value] = curr
    acc = acc + `${key}=${value}`

    if (idx + 1 === array.length) return acc

    return acc + '&'
  }, '')
}

export function downloadReport(path: string, params: any) {
  return openLinkInNewTab(`${appConfig.api.url}/api/statistics/reports/${path}?${stringifyParams(params)}`)
}

export async function downloadReportV2(path: string, filename: string, params: any, callback = undefined) {
  downloadFileAuthorized(`${appConfig.api.url}/api/report/${path}`, filename, params, () => callback && callback())
}

export async function genericDownloadReportV2(path: string, filename: string, params: any, callback = undefined) {
  downloadFileAuthorized(`${appConfig.api.url}/api/${path}`, filename, params, () => callback && callback())
}
export function openLinkInNewTab(url: string, download?: boolean) {
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.target = '_blank'
  anchor.download = download ? '' : undefined

  document.body.appendChild(anchor)
  anchor.click()

  document.body.removeChild(anchor)
}

export function formatString(str: string, ...args: { toString(...args: any): string }[]) {
  let newStr = str
  for (let i = 0; i < args.length; i++) {
    newStr = newStr.replace(`{${i}}`, args[i].toString())
  }

  return newStr
}

export function isOverdueToColor(row) {
  if (
    row.numberOfDocumentsOverdue > 0 ||
    row.numberOfEquipmentDocumentsOverdue > 0 ||
    row.numberOfShipDocumentsOverdue > 0
  ) {
    return '20px solid #b5423e'
  } else if (
    row.numberOfDocumentsCloseToExpire > 0 ||
    row.numberOfEquipmentDocumentsCloseToExpire > 0 ||
    row.numberOfShipDocumentsCloseToExpire > 0
  ) {
    return '20px solid #cdd23d'
  } else {
    return '20px solid #21B573'
  }
}

export function isOverdueToStatus(row) {
  if (
    row.numberOfDocumentsOverdue > 0 ||
    row.numberOfEquipmentDocumentsOverdue > 0 ||
    row.numberOfShipDocumentsOverdue > 0
  ) {
    return 'redBorder'
  } else if (
    row.numberOfDocumentsCloseToExpire > 0 ||
    row.numberOfEquipmentDocumentsCloseToExpire > 0 ||
    row.numberOfShipDocumentsCloseToExpire > 0
  ) {
    return 'yellowBorder'
  } else {
    return 'grayBorder'
  }
}

export function isOverdueWorkorder(row) {
  if (dayjs(row.endDate).startOf('day').isBefore(dayjs().startOf('day'))) {
    return 'overdue'
  }
  return 'notoverdue'
}

export function dynamicSort(property) {
  let sortOrder = 1
  if (property[0] === '-') {
    sortOrder = -1
    property = property.substr(1)
  }
  return function (a, b) {
    /* next line works with strings and numbers,
     * and you may want to customize it to your needs
     */
    const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0
    return result * sortOrder
  }
}

export const groupBy = function (xs, fn) {
  return xs.reduce(function (rv, x) {
    ;(rv[fn(x)] = rv[fn(x)] || []).push(x)
    return rv
  }, {})
}
export const addDays = (date, days) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}
export const addMonths = (date, months) => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  result.setHours(0, 0, 0, 0)
  return result
}

export const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')

export const calculateWorkOrderStatusLabel = (wo: IWorkOrder, t) => {
  switch (wo.workshopBoat?.status) {
    case WorkshopBoatStatus.Pending:
      return t('pending')
    case WorkshopBoatStatus.Accepted:
      return t('accepted')
    case WorkshopBoatStatus.InProgress:
      return t('in-progress')
    case WorkshopBoatStatus.ForApproval:
      return t('to-approval')
    case WorkshopBoatStatus.Completed:
      return t('completed')
    default:
      return t('pending')
  }
}
export const calculateWorkOrderStatusStringLabel = (status: string, t) => {
  switch (status) {
    case IWorkshopStatus.Pending:
      return t('pending')
    case IWorkshopStatus.Accepted:
      return t('accepted')
    case IWorkshopStatus.InProgress:
      return t('in-progress')
    case IWorkshopStatus.ForApproval:
      return t('to-approval')
    case IWorkshopStatus.Completed:
      return t('completed')
    case IWorkshopStatus.Planned:
      // we keeping planned...
      return t('planned')
    default:
      return t('pending')
  }
}

export const statusColors = {
  pending: 'rgb(255,153,0)',
  accepted: 'rgb(14, 240, 52)',
  inProgress: 'rgb(9, 36, 244)',
  forApproval: 'rgb(46,125,50)',
  completed: 'rgb(51,145,30)',
  planned: 'rgb(156, 39, 176)',
  default: 'rgb(182,182,182)',
}

export const calculateWorkOrderStatusColor = (wo: IWorkOrder) => {
  switch (wo.workshopBoat?.status) {
    case WorkshopBoatStatus.Pending:
      return 'rgb(182,182,182)'
    case WorkshopBoatStatus.Accepted:
    case WorkshopBoatStatus.Planned:
      return 'rgb(255,212,89)'
    case WorkshopBoatStatus.InProgress:
      return 'rgb(60,143,189)'
    case WorkshopBoatStatus.ForApproval:
      return 'rgb(128,173,118)'
    case WorkshopBoatStatus.Completed:
      return statusColors.completed
    default:
      return statusColors.default
  }
}
export const calculateWorkOrderStatusStringColor = (status: string) => {
  switch (status) {
    case IWorkshopStatus.Pending:
      return statusColors.pending
    case IWorkshopStatus.Accepted:
    case WorkshopBoatStatus.Planned:
      return 'rgb(25,118,210)'
    case IWorkshopStatus.InProgress:
      return 'rgb(213,226,229)'
    case IWorkshopStatus.ForApproval:
      return statusColors.forApproval
    case IWorkshopStatus.Completed:
      return statusColors.completed
    default:
      return statusColors.default
  }
}
export const calculateWorkOrderStatusStringColorV2 = (status: string) => {
  switch (status) {
    case IWorkshopStatus.Pending:
      return statusColors.pending
    case IWorkshopStatus.Accepted:
      return statusColors.accepted
    case WorkshopBoatStatus.Planned:
      return statusColors.planned
    case IWorkshopStatus.InProgress:
      return statusColors.inProgress
    case IWorkshopStatus.ForApproval:
      return statusColors.forApproval
    case IWorkshopStatus.Completed:
      return statusColors.completed
    default:
      return statusColors.default
  }
}
export const calculateWorkOrderFontColor = (wo: IWorkOrder) => {
  switch (wo.workshopBoat?.status) {
    case WorkshopBoatStatus.InProgress:
    case WorkshopBoatStatus.ForApproval:
    case WorkshopBoatStatus.Completed:
    case WorkshopBoatStatus.Planned:
      return 'white'
    case WorkshopBoatStatus.Pending:
    case WorkshopBoatStatus.Accepted:
    default:
      return 'black'
  }
}
export const calculateWorkOrderStringStatusFontColor = (status: string) => {
  switch (status) {
    case IWorkshopStatus.ForApproval:
    case IWorkshopStatus.Completed:
    case IWorkshopStatus.Planned:
      return 'white'
    case IWorkshopStatus.Pending:
    case IWorkshopStatus.Accepted:
    case IWorkshopStatus.InProgress:
    default:
      return 'black'
  }
}

export const calculateWorkOrderStringStatusFontColorV2 = (status: string) => {
  switch (status) {
    case IWorkshopStatus.InProgress:
    case IWorkshopStatus.ForApproval:
    case IWorkshopStatus.Completed:
    case IWorkshopStatus.Planned:
      return 'white'
    case IWorkshopStatus.Pending:
    case IWorkshopStatus.Accepted:
    default:
      return 'black'
  }
}
export function isDateBeforeToday(date) {
  let parsedDate = date
  if (typeof date === 'string') {
    parsedDate = new Date(date)
  }
  return new Date(parsedDate.toDateString()) < new Date(new Date().toDateString())
}

export function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export function toUppercase(text) {
  if (typeof text === 'string' || text instanceof String) {
    return text.toUpperCase()
  } else {
    return text
  }
}

export function removeEmptyFields(data) {
  Object.keys(data).forEach(key => {
    if (data[key] === '' || data[key] == null) {
      delete data[key]
    }
  })
}

export const renderUrlFriendlyValue = value => {
  if (!value) return ''
  return value
    .replace(/[^a-z0-9_]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}

export const getValueFromStringKey = (value, key) => {
  const keys = key.split('.')
  let valueReturn = value
  keys.forEach(element => {
    valueReturn = valueReturn[element]
  })
  return valueReturn
}

export const formatStringWithObject = (value: any, string: string, keys: string[]) => {
  let returnString = string
  keys.map((key, index) => {
    returnString = returnString.replace(`#${index}#`, getValueFromStringKey(value, key))
  })
  return returnString
}
export const isImageContainer = file => file?.remoteUrl?.includes('vessel-images')
export const isNotEmpty = value => {
  return stripHTML(decode(value)) !== ''
}
export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export const round2Decimals = num => {
  return Math.round(num * 100) / 100
}

export const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

export const getImageUrl = ({ mainImageRemoteUrl, mainImageViewToken, urlFriendlyName, assetType, type }: any) => {
  if (mainImageRemoteUrl) {
    return mainImageRemoteUrl
  }

  if (mainImageViewToken) {
    return `${config.api.url}/api/file/${mainImageViewToken}/${urlFriendlyName}`
  }

  const aType = assetType || type

  switch (aType) {
    case AssetType.Vehicle:
      return 'images/asset/fallback-vehicle.svg'
    case AssetType.Boat:
      return 'images/asset/fallback-vessel.svg'
    case AssetType.FixedAsset:
      return 'images/asset/fallback-fixed-asset.svg'
    case AssetType.Property:
      return 'images/asset/fallback-property.svg'
    default:
      return 'images/place.jpg'
  }
}
export function isJsonString(str) {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}
export const isOlderThanOneHour = datetime => {
  if (!datetime) return false
  if (typeof datetime === 'string') datetime = new Date(datetime)
  const oneHourAgo = new Date(new Date().getTime() - 60 * 60 * 1000)
  return datetime < oneHourAgo
}

const is = (fileName, ext) => new RegExp(`.${ext}`).test(fileName)
export function iconClassName(text) {
  if (is(text, 'pdf')) {
    return 'k-icon k-i-file-pdf'
  } else if (is(text, 'html')) {
    return 'k-icon k-i-html'
  } else if (is(text, 'jpg|png|jpeg')) {
    return 'k-icon k-i-image'
  } else {
    return ''
  }
}

export function getRiskLevelColor(status: number) {
  return matchRiskAnalysisRiskLevel(status, {
    onNotRated: '#757778',
    onLow: '#36b336',
    onModerate: '#f08c00',
    onHigh: '#e32333',
  })
}

export const fontColorFromBackground = hexcolor => {
  const r = parseInt(hexcolor.substring(1, 3), 16)
  const g = parseInt(hexcolor.substring(3, 5), 16)
  const b = parseInt(hexcolor.substring(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 128 ? 'black' : 'white'
}
export function isCloseToExpired(dueDate: string, dueDateNotificationDays: number): boolean {
  if (!dueDate) return false

  const parsedDueDate = new Date(dueDate)
  const notificationDate = new Date(parsedDueDate)
  notificationDate.setDate(parsedDueDate.getDate() - dueDateNotificationDays)

  const today = new Date()
  return today >= notificationDate
}

export const getAvailableWorkcaseStatusArrayByCurrentSection = (
  workshopWorkOrderSection: WorkshopWorkOrderSection,
): WorkshopBoatStatus[] => {
  switch (workshopWorkOrderSection) {
    case WorkshopWorkOrderSection.Planned:
    case WorkshopWorkOrderSection.Users:
    case WorkshopWorkOrderSection.MyWork:
      return [
        WorkshopBoatStatus.Planned,
        WorkshopBoatStatus.Accepted,
        WorkshopBoatStatus.InProgress,
        WorkshopBoatStatus.ForApproval,
      ]
    case WorkshopWorkOrderSection.Open:
      return [WorkshopBoatStatus.Planned, WorkshopBoatStatus.Accepted]
    case WorkshopWorkOrderSection.History:
      return [WorkshopBoatStatus.InProgress, WorkshopBoatStatus.Completed]
    case WorkshopWorkOrderSection.ToApprove:
      return [WorkshopBoatStatus.InProgress, WorkshopBoatStatus.ForApproval, WorkshopBoatStatus.Completed]
    default:
      return []
  }
}

export const getAvailableWorkcaseStatusArrayByCurrentStatus = (status: WorkshopBoatStatus, hasUsers: boolean) => {
  switch (status) {
    case WorkshopBoatStatus.Planned:
      return [WorkshopBoatStatus.Accepted, WorkshopBoatStatus.Planned]
    case WorkshopBoatStatus.Accepted:
      if (hasUsers) {
        return [WorkshopBoatStatus.Accepted, WorkshopBoatStatus.Planned, WorkshopBoatStatus.ForApproval]
      }
      return [WorkshopBoatStatus.Planned, WorkshopBoatStatus.Accepted]
    case WorkshopBoatStatus.InProgress:
      return [
        WorkshopBoatStatus.Planned,
        WorkshopBoatStatus.Accepted,
        WorkshopBoatStatus.InProgress,
        WorkshopBoatStatus.ForApproval,
      ]
    case WorkshopBoatStatus.Completed:
      return [WorkshopBoatStatus.InProgress, WorkshopBoatStatus.Completed]
    case WorkshopBoatStatus.ForApproval:
      return [WorkshopBoatStatus.InProgress, WorkshopBoatStatus.ForApproval, WorkshopBoatStatus.Completed]
    default:
      return []
  }
}

export const getWorkcaseModalType = (status: WorkshopBoatStatus, hasUsers: boolean) => {
  switch (status) {
    // remove planned status
    case WorkshopBoatStatus.Planned:
    case WorkshopBoatStatus.Accepted:
      if (hasUsers) {
        return WorkshopBoatStatus.Accepted
      }
      return WorkshopBoatStatus.Open
    case WorkshopBoatStatus.InProgress:
      return WorkshopBoatStatus.InProgress
    case WorkshopBoatStatus.Completed:
      return WorkshopBoatStatus.Completed
    case WorkshopBoatStatus.ForApproval:
      return WorkshopBoatStatus.ForApproval
    default:
      return []
  }
}
export const getAssetNameByType = (type: number, t: TFunction<'translation', undefined>) => {
  switch (type) {
    case AssetType.Boat:
      return t('asset:vessel')
    case AssetType.FixedAsset:
      return t('asset:fixed-asset')
    case AssetType.Vehicle:
      return t('asset:vehicle')
    case AssetType.Property:
      return t('asset:property')
    default:
      return ''
  }
}

export const getAssetTypeLabel = (
  type: number,
  vesselType: MainVesselType | undefined,
  t: TFunction<'translation', undefined>,
) => {
  if (type === AssetType.Boat && vesselType) {
    return vesselType === MainVesselType.Barge ? t('boat:barge') : t('boat:boat')
  }
  return getAssetNameByType(type, t)
}

export const toAssetDataLabel = (asset: IBoat): IAssetDataLabel => ({
  assetId: asset.id ?? asset.boatId,
  name: asset.name,
  type: asset.assetType,
  vesselType: asset.vesselType,
  clientId: asset.clientId,
})

export const getAssetTitle = (asset: IAsset): string => {
  if (!asset) return ''

  switch (asset.type) {
    case AssetType.Boat:
      return asset.name
    case AssetType.Property:
      return asset.property?.addressLine1
    case AssetType.Vehicle:
      return asset.vehicle?.registrationNumber
    case AssetType.FixedAsset:
      return asset.fixedAsset?.registrationNumber
    default:
      return asset.name
  }
}

export const toUtcDateOnly = (value: any) => {
  const sourceDate = dayjs.isDayjs(value) ? value.toDate() : new Date(value)
  if (Number.isNaN(sourceDate.getTime())) return null
  return new Date(Date.UTC(sourceDate.getFullYear(), sourceDate.getMonth(), sourceDate.getDate(), 0, 0, 0, 0))
}

export const assetTypes = [
  { id: AssetType.Boat, name: t('asset:vessel') },
  { id: AssetType.FixedAsset, name: t('asset:fixed-asset') },
  { id: AssetType.Vehicle, name: t('asset:vehicle') },
  { id: AssetType.Property, name: t('asset:property') },
]

export function getCustomizedApiErrorMessage(error: any, translate: TFunction, namespace: string): string {
  const status = error?.response?.status
  const fallback = translate('common:failed-operation-message')

  if (!status || status >= 500) return fallback

  const data = error?.response?.data
  const backendErrorKey = typeof data === 'string' ? data : data?.Error ?? data?.Message
  if (typeof backendErrorKey !== 'string' || backendErrorKey.trim() === '') return fallback

  return translate(`${namespace}:${backendErrorKey}`, { defaultValue: fallback })
}

export const parseRepeatWeekdays = (repeatWeekdays?: string | number[]) => {
  if (!repeatWeekdays) return []

  if (Array.isArray(repeatWeekdays)) {
    return Array.from(new Set(repeatWeekdays)).sort((a, b) => a - b)
  }
  return repeatWeekdays
    .split(',')
    .filter(Boolean)
    .map(weekday => Number(weekday.trim()))
    .filter(weekday => Number.isInteger(weekday) && weekday >= 1 && weekday <= 7)
    .sort((a, b) => a - b)
}

export const getNextWeekdayOccurrence = (baseDate: Date, repeatWeek: number, repeatWeekdays: number[]) => {
  if (repeatWeekdays.length === 0) {
    return dayjs(baseDate).add(repeatWeek, 'week').toDate()
  }

  const currentWeekday = baseDate.getDay() === 0 ? 7 : baseDate.getDay()

  const nextWeekdayInCurrentCycle = repeatWeekdays.find(weekday => weekday > currentWeekday)
  if (repeatWeekdays.includes(currentWeekday) && nextWeekdayInCurrentCycle) {
    return dayjs(baseDate)
      .add(nextWeekdayInCurrentCycle - currentWeekday, 'day')
      .toDate()
  }

  const daysFromMonday = currentWeekday - 1
  const currentCycleMonday = dayjs(baseDate).subtract(daysFromMonday, 'day')
  const nextCycleMonday = currentCycleMonday.add(repeatWeek, 'week')

  return nextCycleMonday.add(repeatWeekdays[0] - 1, 'day').toDate()
}

export const getSmartRepeatStart = (service: Service) => {
  const today = toUtcDateOnly(new Date())
  if (service.repeatYear) {
    const repeatStart = dayjs(today).add(service.repeatYear, 'year').toDate()
    return repeatStart
  }
  if (service.repeatMonth) {
    const targetDate = dayjs(today).add(Number(service.repeatMonth), 'month')

    if (service.repeatDay) {
      const repeatDay = Number(service.repeatDay)
      const safeDay = Math.min(repeatDay, targetDate.daysInMonth())

      return targetDate.date(safeDay).toDate()
    }

    return targetDate.toDate()
  }
  if (service.repeatWeek) {
    return getNextWeekdayOccurrence(today, Number(service.repeatWeek), parseRepeatWeekdays(service.repeatWeekdays))
  }
  if (service.repeatDay) {
    const repeatStart = dayjs(today).add(service.repeatDay, 'day').toDate()
    return repeatStart
  }
  return today
}
